import React, { useEffect, useState } from "react";
import { Bell, Building2, HeartHandshake, ShoppingBag, Flag, X, MessageSquareWarning, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccInput, ccBtnPink } from "./care/ui";
import { LogDetailModal } from "./lib/LogDetail";

const API = process.env.REACT_APP_BACKEND_URL;

const ALERT_META = {
  facility_signup: { icon: Building2, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", label: "Facility Signup" },
  caregiver_signup: { icon: HeartHandshake, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]", label: "Caregiver Signup" },
  store_purchase: { icon: ShoppingBag, wrap: "bg-[#E2F4E1]", color: "text-[#2F6031]", label: "Store Purchase" },
  complaint: { icon: Flag, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", label: "Complaint" },
  compliance: { icon: Flag, wrap: "bg-rose-50", color: "text-rose-400", label: "Compliance" },
};

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// ---------- Broadcast center (create + manage announcements) ----------
export const BroadcastCenter = () => {
  const [form, setForm] = useState({ title: "", message: "", audience: "all", channels: ["internal"], customEmails: "" });
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    fetch(`${API}/api/master/broadcasts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setList)
      .catch((err) => console.debug("broadcasts fetch failed", err));

  useEffect(() => {
    load();
  }, []);

  const toggleChannel = (ch) =>
    setForm((f) => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.channels.length === 0) {
      toast.error("Pick at least one channel: popup, email or SMS.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/master/broadcasts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: form.title, message: form.message, audience: form.audience, channels: form.channels, custom_emails: form.customEmails.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Broadcast failed");
      toast.success(`Broadcast dispatched via ${form.channels.join(" + ")} — deliveries tracked in the Communications Log.`);
      setForm({ title: "", message: "", audience: "all", channels: ["internal"], customEmails: "" });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const retire = async (id) => {
    try {
      const res = await fetch(`${API}/api/master/broadcasts/${id}/retire`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Broadcast retired.");
      load();
    } catch {
      toast.error("Could not retire broadcast.");
    }
  };

  return (
    <div className={`${ccCard} p-6`} data-testid="broadcast-center">
      <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
          <Megaphone className="w-4 h-4 text-[#E5A0BC]" />
        </span>
        Broadcast Engine
      </h3>
      <form onSubmit={submit} className="space-y-3 mb-5" data-testid="broadcast-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Announcement title" className={ccInput} data-testid="bc-title" />
          <input type="text" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message body" className={ccInput} data-testid="bc-message" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={`${ccInput} !w-auto`} data-testid="bc-audience">
            <option value="all">Everyone</option>
            <option value="parents">Parents</option>
            <option value="staff">Staff</option>
            <option value="all_staff">All Staff (incl. daycare admins)</option>
            <option value="caregivers">Caregivers</option>
            <option value="daycare_admins">Daycare Admins</option>
            <option value="custom_list">Custom Email List</option>
          </select>
          {[
            ["internal", "Popup"],
            ["email", "Email"],
            ["sms", "SMS"],
          ].map(([ch, label]) => (
            <label key={ch} className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-[11px] font-bold cursor-pointer transition-all ${form.channels.includes(ch) ? "bg-[#374151] text-white border-[#374151]" : "bg-white border-[#FCE6E9] text-[#6B7280] hover:border-[#F8C8DC] dark:bg-[#273244] dark:border-[#374457]"}`} data-testid={`bc-channel-${ch}`}>
              <input type="checkbox" className="sr-only" checked={form.channels.includes(ch)} onChange={() => toggleChannel(ch)} />
              {label}
            </label>
          ))}
          <button type="submit" disabled={busy} className={`${ccBtnPink} shrink-0 disabled:opacity-50 sm:ml-auto`} data-testid="bc-submit">
            {busy ? "Sending…" : "Broadcast"}
          </button>
        </div>
        {form.audience === "custom_list" && (
          <textarea value={form.customEmails} onChange={(e) => setForm({ ...form, customEmails: e.target.value })} placeholder="Recipient emails — one per line or comma-separated" rows={3} className={`${ccInput} resize-none`} data-testid="bc-custom-emails" />
        )}
      </form>
      {list === null ? (
        <p className="text-xs text-[#9CA3AF] font-semibold">Loading broadcasts…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="bc-empty">No broadcasts yet — your announcements appear here.</p>
      ) : (
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {list.map((b) => (
            <div key={b.id} className={`flex items-center justify-between gap-3 p-3 rounded-2xl border ${b.active ? "bg-[#FFF9F2] border-[#FCE6E9]" : "bg-gray-50 border-gray-200 opacity-60"} dark:bg-[#273244] dark:border-[#374457]`} data-testid={`bc-row-${b.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">
                  {b.title}
                  <span className={`ml-2 px-2 py-0.5 border text-[9px] font-extrabold uppercase rounded-full ${b.active ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-gray-100 border-gray-200 text-gray-400"}`}>
                    {b.active ? "Live" : "Retired"}
                  </span>
                </p>
                <p className="text-[10px] text-[#9CA3AF] font-medium truncate">
                  → {b.audience.replace("_", " ")} · via {(b.channels || ["internal"]).join("+")} · {b.dismissed_count} dismissed · {new Date(b.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
              {b.active && (
                <button onClick={() => retire(b.id)} data-testid={`bc-retire-${b.id}`} className="px-3 py-1.5 bg-white border border-[#F8C8DC] rounded-full text-[10px] font-bold text-[#B04A6B] hover:bg-[#FCE6E9] shrink-0">
                  Retire
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Real-time platform alert feed ----------
export const PlatformAlerts = () => {
  const [alerts, setAlerts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = () =>
    fetch(`${API}/api/master/alerts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAlerts)
      .catch((err) => console.debug("alerts fetch failed", err));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const dismiss = async (id) => {
    try {
      const res = await fetch(`${API}/api/master/alerts/${id}/dismiss`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    } catch {
      toast.error("Could not dismiss alert.");
    }
  };

  const unread = (alerts || []).filter((a) => !a.read).length;

  return (
    <div className={`${ccCard} p-6`} data-testid="master-alerts">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
            <Bell className="w-4 h-4 text-[#E5A0BC]" />
          </span>
          Platform Alert Feed
          {unread > 0 && (
            <span className="px-2.5 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold rounded-full" data-testid="master-alerts-unread">
              {unread} new
            </span>
          )}
        </h3>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          data-testid="master-alerts-autorefresh"
          className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${autoRefresh ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-white border-[#FCE6E9] text-[#9CA3AF]"}`}
        >
          {autoRefresh ? "Live · 15s" : "Paused"}
        </button>
      </div>
      {alerts === null ? (
        <p className="text-xs text-[#9CA3AF] font-semibold">Loading alerts…</p>
      ) : alerts.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="master-alerts-empty">No platform events yet — signups, purchases, and complaints appear here in real time.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {alerts.map((a) => {
            const meta = ALERT_META[a.type] || ALERT_META.compliance;
            return (
              <div
                key={a.id}
                onClick={() => setSelected(a)}
                className={`flex items-center gap-3 p-3 rounded-2xl border transition-colors cursor-pointer hover:border-[#B3E0F2] ${a.read ? "bg-[#FFF9F2]/50 border-[#FCE6E9] opacity-60" : "bg-[#FFF9F2] border-[#FCE6E9]"} dark:bg-[#273244] dark:border-[#374457]`}
                data-testid={`master-alert-row-${a.id}`}
              >
                <span className={`w-8 h-8 rounded-full ${meta.wrap} flex items-center justify-center shrink-0`}>
                  <meta.icon className={`w-3.5 h-3.5 ${meta.color}`} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#374151] dark:text-slate-100 truncate">{a.message}</p>
                  <p className="text-[10px] text-[#9CA3AF] font-medium">{meta.label} · {fmtTs(a.created_at)}</p>
                </div>
                {a.type === "live_access" && !a.read && (
                  <>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await fetch(`${API}/api/master/live-access/${a.meta?.request_id}/approve`, { method: "POST", credentials: "include" });
                        toast.success("Public live approved — they can host now.");
                        load();
                      }}
                      data-testid={`master-alert-approve-${a.id}`}
                      className="px-2.5 py-1 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[9px] font-extrabold uppercase rounded-full shrink-0 hover:bg-[#C4E5C2] transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await fetch(`${API}/api/master/live-access/${a.meta?.request_id}/decline`, { method: "POST", credentials: "include" });
                        toast.success("Request declined.");
                        load();
                      }}
                      data-testid={`master-alert-decline-${a.id}`}
                      className="px-2.5 py-1 bg-white border border-rose-200 text-rose-400 text-[9px] font-extrabold uppercase rounded-full shrink-0 hover:bg-rose-50 transition-colors"
                    >
                      Decline
                    </button>
                  </>
                )}
                {!a.read && (
                  <button onClick={(e) => { e.stopPropagation(); dismiss(a.id); }} data-testid={`master-alert-dismiss-${a.id}`} className="w-7 h-7 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {selected && (
        <LogDetailModal
          title={selected.type || "Platform event"}
          fields={[
            { label: "Message", value: selected.message },
            { label: "Type", value: selected.type },
            { label: "Time", value: selected.created_at ? new Date(selected.created_at).toLocaleString() : "—" },
            { label: "Status", value: selected.read ? "Read" : "Unread" },
            { label: "Event ID", value: selected.id },
          ]}
          json={selected.data && Object.keys(selected.data).length ? selected.data : null}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};
export const ComplaintsOversight = () => {
  const [complaints, setComplaints] = useState(null);
  const [regionals, setRegionals] = useState([]);

  const load = () =>
    fetch(`${API}/api/master/complaints`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setComplaints)
      .catch((err) => console.debug("complaints fetch failed", err));

  useEffect(() => {
    load();
    fetch(`${API}/api/master/regionals`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { regionals: [] }))
      .then((d) => setRegionals(d.regionals || []))
      .catch(() => {});
  }, []);

  const elevate = async (cid) => {
    const res = await fetch(`${API}/api/master/complaints/${cid}/elevate`, { method: "PATCH", credentials: "include" });
    if (res.ok) { toast.success("Elevated to super admin."); load(); }
    else { const b = await res.json().catch(() => ({})); toast.error(b.detail || "Could not elevate"); }
  };

  const assign = async (cid, email) => {
    const res = await fetch(`${API}/api/master/complaints/${cid}/assign`, {
      method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }),
    });
    if (res.ok) { toast.success(`Assigned to ${email}.`); load(); }
    else { const b = await res.json().catch(() => ({})); toast.error(b.detail || "Could not assign"); }
  };

  const escalated = (complaints || []).filter((c) => c.escalated).length;

  return (
    <div className={`${ccCard} p-6`} data-testid="master-complaints">
      <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5 mb-1">
        <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
          <MessageSquareWarning className="w-4 h-4 text-[#D9A93E]" />
        </span>
        Complaints Oversight
        {escalated > 0 && (
          <span className="px-2.5 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold rounded-full" data-testid="master-complaints-escalated-count">
            {escalated} escalated
          </span>
        )}
      </h3>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
        Facility-first routing: track when each complaint was filed, how long the facility took to respond, and final resolution. Items unresolved for 48+ hours are flagged escalated.
      </p>
      {complaints === null ? (
        <p className="text-xs text-[#9CA3AF] font-semibold">Loading complaints…</p>
      ) : complaints.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="master-complaints-empty">No complaints filed anywhere on the platform.</p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {complaints.map((c) => (
            <div key={c.id} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`master-complaint-row-${c.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-black text-[#374151] dark:text-slate-100">
                  {c.subject} <span className="text-[#9CA3AF] font-bold">· {c.facilityName}</span>
                </p>
                <div className="flex items-center gap-1.5">
                  {c.escalated && (
                    <span className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold uppercase rounded-full animate-pulse" data-testid={`master-complaint-escalated-${c.id}`}>
                      Escalated
                    </span>
                  )}
                  <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${
                    c.status === "resolved" ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : c.status === "responded" ? "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]" : "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]"
                  }`} data-testid={`master-complaint-status-${c.id}`}>
                    {c.status}
                  </span>
                </div>
              </div>
              <p className="text-[11px] text-[#9CA3AF] font-medium mt-1.5">
                {c.fromName || c.fromEmail} · filed {fmtTs(c.createdAt)} ·{" "}
                response: {c.response_minutes !== null ? `${c.response_minutes}m` : "awaiting"} ·{" "}
                resolution: {c.resolution_hours !== null ? `${c.resolution_hours}h` : "—"}
              </p>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="px-2 py-0.5 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full" data-testid={`master-complaint-region-${c.id}`}>
                  {c.region ? `Region: ${c.region}` : "No region → super admin"}
                </span>
                <span className="px-2 py-0.5 bg-white border border-[#FCE6E9] text-[#9CA3AF] text-[9px] font-extrabold uppercase rounded-full">
                  Reviewer: {c.assigned_to || "Super admin"}
                </span>
                {c.elevated && (
                  <span className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[9px] font-extrabold uppercase rounded-full" data-testid={`master-complaint-elevated-${c.id}`}>
                    Elevated to super admin
                  </span>
                )}
                {!c.elevated && c.status !== "resolved" && (
                  <button onClick={() => elevate(c.id)} data-testid={`master-complaint-elevate-${c.id}`} className="px-2 py-0.5 bg-white border border-rose-200 text-rose-500 text-[9px] font-extrabold uppercase rounded-full hover:bg-rose-50 transition-colors">
                    Elevate
                  </button>
                )}
                {regionals.length > 0 && (
                  <select defaultValue="" onChange={(e) => e.target.value && assign(c.id, e.target.value)} data-testid={`master-complaint-assign-${c.id}`} className="px-2 py-0.5 bg-white border border-[#FCE6E9] text-[#374151] text-[9px] font-extrabold uppercase rounded-full">
                    <option value="">Assign…</option>
                    {regionals.map((r) => (
                      <option key={r.email} value={r.email}>{r.name || r.email} ({r.states.join("/")})</option>
                    ))}
                  </select>
                )}
                <a href={`${API}/api/master/complaints/${c.id}/report`} target="_blank" rel="noreferrer" data-testid={`master-complaint-pdf-${c.id}`} className="px-2 py-0.5 bg-white border border-[#B3E0F2] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full hover:bg-[#E6F2FA] transition-colors">
                  PDF Report
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
